import { ChangeEvent, FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  ModalProps,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { PutFeeDTO, useGetCoinsQuery, useGetFeeLevelsQuery } from '../../../../api';
import { Option, FeeDirection } from '../../../../enums';
import { useParams } from 'react-router-dom';

type CreateFeeModalProps = {
  open: boolean;
  onClose: () => void;
  onCreate: () => void;
  fields: PutFeeDTO['body'] | null;
  fieldsHandler: (
    e: SelectChangeEvent | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  isLoading: boolean;
};

export const CreateFeeModal: FC<CreateFeeModalProps> = (props) => {
  const { open, onClose, onCreate, fields, fieldsHandler, isLoading } = props;
  const { t } = useTranslation();
  const { fee_group_id } = useParams();

  // api
  const { data: coinList, isLoading: isCoinsLoading } = useGetCoinsQuery();
  const { data: feeLevelList, isLoading: isFeeLevelsLoading } = useGetFeeLevelsQuery({
    fee_group_id: Number(fee_group_id),
  });

  // validation for required fields
  const requiredFields = [
    'coin_id',
    'type',
    'fixed_amount',
    'percent',
    'option',
    'fee_level_id',
  ] as (keyof PutFeeDTO['body'])[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || fields[field] === '') {
        return true;
      }
    }

    if (Number(fields.fixed_amount) < 0 || Number(fields.percent) < 0) return true;

    return false;
  };

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    onClose();
  };
  const closeHandler = () => {
    onClose();
  };

  // elements
  const coinSelectOptions = coinList?.coins.map((coin) => {
    return (
      <MenuItem key={coin.id} value={coin.id}>
        {coin.name}
      </MenuItem>
    );
  });

  const sortedFeeLevels = useMemo(() => {
    if (!feeLevelList?.feeLevels) return [];
    return [...feeLevelList.feeLevels].sort((a, b) => a.min_turnover - b.min_turnover);
  }, [feeLevelList?.feeLevels]);

  const feeLevelsSelectOptions = sortedFeeLevels.map((level: any) => {
    return (
      <MenuItem key={level.id} value={level.id}>
        {level.min_turnover}
      </MenuItem>
    );
  });

  const directionSelectOptions = Object.values(FeeDirection).map((it) => {
    return (
      <MenuItem key={it} value={it}>
        {t(`common.fee-direction.${it}`)}
      </MenuItem>
    );
  });

  const optionSelectOptions = Object.values(Option).map((it) => {
    return (
      <MenuItem key={it} value={it}>
        {it}
      </MenuItem>
    );
  });

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>{t('fees.modals.create.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            select
            name="fee_level_id"
            value={fields?.fee_level_id ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('fees.modals.create.fields.fee-level')}
            fullWidth
          >
            {feeLevelsSelectOptions}
          </TextField>

          <TextField
            select
            name="coin_id"
            value={fields?.coin_id ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('fees.modals.create.fields.coin')}
            fullWidth
          >
            {coinSelectOptions}
          </TextField>

          <TextField
            select
            name="type"
            value={fields?.type ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('fees.modals.create.fields.type')}
            fullWidth
          >
            {directionSelectOptions}
          </TextField>

          <TextField
            select
            name="option"
            value={fields?.option ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('fees.modals.create.fields.option')}
            fullWidth
          >
            {optionSelectOptions}
          </TextField>

          <TextField
            name="fixed_amount"
            type="number"
            value={fields?.fixed_amount ?? ''}
            onChange={(e) => fieldsHandler(e)}
            inputProps={{ min: 0, step: '0.01' }}
            label={t('fees.modals.create.fields.fixed-amount')}
            fullWidth
          />

          <TextField
            name="percent"
            type="number"
            value={fields?.percent ?? ''}
            onChange={(e) => fieldsHandler(e)}
            inputProps={{ min: 0, step: '0.01' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            label={t('fees.modals.create.fields.percent')}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onCreate()}
          disabled={isCoinsLoading || isLoading || checkSubmitDisabling()}
        >
          {t('fees.modals.create.create-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onClose()}>
          {t('fees.modals.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
